import { FirebaseError } from 'firebase/app'
import { doc, getDoc } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import app, { db } from './firebase'
import { mapPwfActiveTask } from './pwfActiveTasksService'
import {
  mapPwfCompletedTask,
  mapPwfProjectProfitability,
} from './pwfProfitabilityService'
import type {
  ActiveTaskEntry,
  ProjectProfitabilityEntry,
  TimeLogEntry,
} from '../components/ProfitabilityPerformance/mockData'
import type {
  PwfActiveTaskRaw,
  PwfActiveTasksSummary,
  PwfCompletedTaskRaw,
  PwfEmployeeTaskTime,
  PwfEmployeeTimeSpent,
  PwfProfitabilitySnapshotDoc,
  PwfProfitabilitySnapshotPartDoc,
  SavePwfProfitabilitySnapshotResponse,
} from '../types'

export const PWF_PROFITABILITY_COLLECTION = 'pwfProfitability'
export const PWF_PROFITABILITY_LATEST = 'latest'

export type ProfitabilitySnapshotData = {
  completedTasks: TimeLogEntry[]
  projectProfitability: ProjectProfitabilityEntry[]
  activeTasks: ActiveTaskEntry[]
  activeTasksSummary: PwfActiveTasksSummary | null
  employeeTaskTime: PwfEmployeeTaskTime[]
  employeeTimeSpent: PwfEmployeeTimeSpent[]
  employeeTimeSpentMtd: PwfEmployeeTimeSpent[]
  employeeTimeSpentByMonth: Record<string, PwfEmployeeTimeSpent[]>
  employeeTimeSpentByDay: Record<string, PwfEmployeeTimeSpent[]>
  mtdTimeTrackedFrom?: string
  mtdTimeTrackedTo?: string
  taskCompletionDates: Record<string, string>
  mtdInvoiceTotal: number
  recordedAt: string
}

function friendlySnapshotError(err: unknown): string {
  if (err instanceof FirebaseError) {
    if (err.code === 'functions/deadline-exceeded') {
      return 'ProWorkflow fetch timed out (9 min limit). Check Firebase Functions logs for savePwfProfitabilitySnapshot.'
    }
    if (err.code === 'functions/permission-denied') {
      return 'Only admins can fetch a new snapshot from ProWorkflow.'
    }
    if (err.code === 'functions/unauthenticated') {
      return 'Sign in to load ProWorkflow data.'
    }
    if (err.code === 'functions/not-found') {
      return 'savePwfProfitabilitySnapshot is not deployed yet. Run: npx firebase-tools deploy --only functions:savePwfProfitabilitySnapshot'
    }
    if (err.code === 'functions/internal' && err.message) {
      return err.message
    }
  }

  if (err instanceof Error) {
    const msg = err.message
    if (msg.includes('deadline-exceeded') || msg.includes('timeout') || msg.includes('504')) {
      return 'ProWorkflow fetch timed out. This can take several minutes — check Cloud Function logs if it keeps failing.'
    }
    if (msg.includes('not-found') || msg.includes('NOT_FOUND')) {
      return 'savePwfProfitabilitySnapshot is not deployed yet. Run: npx firebase-tools deploy --only functions:savePwfProfitabilitySnapshot'
    }
    if (msg.includes('permission-denied') || msg.includes('PERMISSION_DENIED')) {
      return 'Only admins can fetch a new snapshot from ProWorkflow.'
    }
    if (msg.includes('unauthenticated') || msg.includes('UNAUTHENTICATED')) {
      return 'Sign in to load ProWorkflow data.'
    }
    return msg
  }
  return 'Failed to load ProWorkflow profitability snapshot'
}

async function fetchSnapshotPartItems(docIds: readonly string[]): Promise<unknown[]> {
  if (docIds.length === 0) return []
  const snaps = await Promise.all(
    docIds.map((id) => getDoc(doc(db, PWF_PROFITABILITY_COLLECTION, id)))
  )
  const parts: PwfProfitabilitySnapshotPartDoc[] = []
  for (const partSnap of snaps) {
    if (!partSnap.exists()) continue
    const part = partSnap.data() as PwfProfitabilitySnapshotPartDoc
    if (Array.isArray(part.items)) parts.push(part)
  }
  parts.sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
  return parts.flatMap((part) => part.items)
}

function taskCompletionDatesFromPartEntries(
  entries: unknown[]
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const entry of entries) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue
    const { id, date } = entry as { id?: string; date?: string }
    if (typeof id === 'string' && typeof date === 'string') {
      out[id] = date
    }
  }
  return out
}

export type ActiveWorkOverviewData = {
  recordedAt: string | null
  activeTasksSummary: PwfActiveTasksSummary | null
  mtdInvoiceTotal: number
  activeTasks: ActiveTaskEntry[]
}

/** Lightweight read for dashboard executive summary (active tasks only). */
export async function fetchActiveWorkOverviewFromFirestore(): Promise<ActiveWorkOverviewData | null> {
  const snap = await getDoc(doc(db, PWF_PROFITABILITY_COLLECTION, PWF_PROFITABILITY_LATEST))
  if (!snap.exists()) return null

  const data = snap.data() as Partial<PwfProfitabilitySnapshotDoc>
  const chunks = data.chunks

  let activeTasksRaw: PwfActiveTaskRaw[]
  if (chunks?.activeTasks?.length) {
    const activeItems = await fetchSnapshotPartItems(chunks.activeTasks)
    activeTasksRaw = activeItems as PwfActiveTaskRaw[]
  } else {
    activeTasksRaw = data.activeTasks ?? []
  }

  return {
    recordedAt: data.recordedAt ?? null,
    activeTasksSummary: data.activeTasksSummary ?? null,
    mtdInvoiceTotal: data.mtdInvoiceTotal ?? 0,
    activeTasks: activeTasksRaw.map(mapPwfActiveTask),
  }
}

/** Lightweight read of project profitability rows from the latest PWF snapshot. */
export async function fetchProjectProfitabilityFromFirestore(): Promise<{
  projectProfitability: ProjectProfitabilityEntry[]
  recordedAt: string | null
} | null> {
  const snap = await getDoc(doc(db, PWF_PROFITABILITY_COLLECTION, PWF_PROFITABILITY_LATEST))
  if (!snap.exists()) return null

  const data = snap.data() as Partial<PwfProfitabilitySnapshotDoc>
  return {
    projectProfitability: (data.projectProfitability ?? []).map(mapPwfProjectProfitability),
    recordedAt: data.recordedAt ?? null,
  }
}

export async function fetchProfitabilitySnapshotFromFirestore(): Promise<ProfitabilitySnapshotData> {
  const snap = await getDoc(doc(db, PWF_PROFITABILITY_COLLECTION, PWF_PROFITABILITY_LATEST))
  if (!snap.exists()) {
    throw new Error(
      'No ProWorkflow snapshot yet. An admin can use Fetch from PWF to create the first snapshot.'
    )
  }

  const data = snap.data() as Partial<PwfProfitabilitySnapshotDoc>
  const recordedAt = data.recordedAt ?? new Date().toISOString().split('T')[0]
  const chunks = data.chunks

  let completedTasksRaw: PwfCompletedTaskRaw[]
  let activeTasksRaw: PwfActiveTaskRaw[]
  let employeeTaskTime: PwfEmployeeTaskTime[]
  let taskCompletionDates: Record<string, string>

  if (chunks) {
    const [completedItems, activeItems, employeeTaskTimeItems, taskDateItems] =
      await Promise.all([
        fetchSnapshotPartItems(chunks.completedTasks ?? []),
        fetchSnapshotPartItems(chunks.activeTasks ?? []),
        fetchSnapshotPartItems(chunks.employeeTaskTime ?? []),
        fetchSnapshotPartItems(chunks.taskCompletionDates ?? []),
      ])
    completedTasksRaw = completedItems as PwfCompletedTaskRaw[]
    activeTasksRaw = activeItems as PwfActiveTaskRaw[]
    employeeTaskTime = employeeTaskTimeItems as PwfEmployeeTaskTime[]
    taskCompletionDates = taskCompletionDatesFromPartEntries(taskDateItems)
  } else {
    completedTasksRaw = data.completedTasks ?? []
    activeTasksRaw = data.activeTasks ?? []
    employeeTaskTime = data.employeeTaskTime ?? []
    taskCompletionDates = data.taskCompletionDates ?? {}
  }

  return {
    completedTasks: completedTasksRaw.map(mapPwfCompletedTask),
    projectProfitability: (data.projectProfitability ?? []).map(mapPwfProjectProfitability),
    activeTasks: activeTasksRaw.map(mapPwfActiveTask),
    activeTasksSummary: data.activeTasksSummary ?? null,
    employeeTaskTime,
    employeeTimeSpent: data.employeeTimeSpent ?? [],
    employeeTimeSpentMtd: data.employeeTimeSpentMtd ?? [],
    employeeTimeSpentByMonth: data.employeeTimeSpentByMonth ?? {},
    employeeTimeSpentByDay: data.employeeTimeSpentByDay ?? {},
    mtdTimeTrackedFrom: data.mtdTimeTrackedFrom,
    mtdTimeTrackedTo: data.mtdTimeTrackedTo,
    taskCompletionDates,
    mtdInvoiceTotal: data.mtdInvoiceTotal ?? 0,
    recordedAt,
  }
}

const SNAPSHOT_CALL_TIMEOUT_MS = 300_000

export async function savePwfProfitabilitySnapshotFromPwf(): Promise<SavePwfProfitabilitySnapshotResponse> {
  const functions = getFunctions(app)
  const savePwfProfitabilitySnapshot = httpsCallable<
    unknown,
    SavePwfProfitabilitySnapshotResponse
  >(functions, 'savePwfProfitabilitySnapshot', { timeout: SNAPSHOT_CALL_TIMEOUT_MS })

  try {
    const result = await savePwfProfitabilitySnapshot({})
    const data = result.data
    if (!data?.success) {
      throw new Error('ProWorkflow returned an unexpected response')
    }
    return data
  } catch (err: unknown) {
    throw new Error(friendlySnapshotError(err))
  }
}

export function filterCompletedTasksByDateRange(
  tasks: TimeLogEntry[],
  dateRange: { from: string; to: string }
): TimeLogEntry[] {
  return tasks.filter((t) => {
    if (dateRange.from && t.date < dateRange.from) return false
    if (dateRange.to && t.date > dateRange.to) return false
    return true
  })
}

function toYyyyMmDdLocal(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** First through last day of the calendar month containing `ref` (local timezone). */
export function getCalendarMonthRange(ref: Date = new Date()): { from: string; to: string } {
  const y = ref.getFullYear()
  const m = ref.getMonth()
  return {
    from: toYyyyMmDdLocal(new Date(y, m, 1)),
    to: toYyyyMmDdLocal(new Date(y, m + 1, 0)),
  }
}

/** First day of current calendar month through today (inclusive, local timezone). */
export function getMonthToDateRange(ref: Date = new Date()): { from: string; to: string } {
  const y = ref.getFullYear()
  const m = ref.getMonth()
  return {
    from: toYyyyMmDdLocal(new Date(y, m, 1)),
    to: toYyyyMmDdLocal(ref),
  }
}

export function isFullCalendarMonthRange(from: string, to: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return false
  const month = getCalendarMonthRange(new Date(`${from}T12:00:00`))
  return from === month.from && to === month.to
}

function addDaysYyyyMmDd(dateYmd: string, days: number): string {
  const d = new Date(`${dateYmd}T12:00:00`)
  d.setDate(d.getDate() + days)
  return toYyyyMmDdLocal(d)
}

function listDatesInclusive(from: string, to: string): string[] {
  const dates: string[] = []
  let cur = from
  while (cur <= to) {
    dates.push(cur)
    cur = addDaysYyyyMmDd(cur, 1)
  }
  return dates
}

function sumEmployeeTimeRows(rows: readonly PwfEmployeeTimeSpent[]): PwfEmployeeTimeSpent[] {
  const byContact = new Map<number, PwfEmployeeTimeSpent>()
  for (const row of rows) {
    const cur = byContact.get(row.contactId) ?? {
      contactId: row.contactId,
      name: row.name,
      recordedHours: 0,
    }
    cur.recordedHours = Math.round((cur.recordedHours + row.recordedHours) * 10) / 10
    byContact.set(row.contactId, cur)
  }
  return [...byContact.values()].sort(
    (a, b) => b.recordedHours - a.recordedHours || a.name.localeCompare(b.name)
  )
}

/** Sum daily snapshot buckets for a custom tracked-time range. */
export function sumEmployeeTimeFromDailyBuckets(
  from: string,
  to: string,
  byDay: Record<string, PwfEmployeeTimeSpent[]>
): PwfEmployeeTimeSpent[] | null {
  const dayKeys = Object.keys(byDay)
  if (dayKeys.length === 0) return null
  const sorted = dayKeys.sort()
  const oldest = sorted[0]
  const newest = sorted[sorted.length - 1]

  const overlapFrom = from > oldest ? from : oldest
  const overlapTo = to < newest ? to : newest
  if (overlapFrom > overlapTo) return null

  const rows: PwfEmployeeTimeSpent[] = []
  for (const date of listDatesInclusive(overlapFrom, overlapTo)) {
    const dayRows = byDay[date]
    if (dayRows?.length) rows.push(...dayRows)
  }
  return sumEmployeeTimeRows(rows)
}

export function customTrackedTimeUnavailableMessage(
  from: string,
  to: string,
  snapshot: Pick<
    ProfitabilitySnapshotData,
    'employeeTimeSpentByDay' | 'employeeTimeSpentByMonth'
  > | null
): string {
  const hasDaily = Object.keys(snapshot?.employeeTimeSpentByDay ?? {}).length > 0
  const hasMonth = Object.keys(snapshot?.employeeTimeSpentByMonth ?? {}).length > 0
  if (!hasDaily && !hasMonth) {
    return (
      'Custom tracked time needs a fresh PWF snapshot. An admin should deploy the latest ' +
      'functions, then click Fetch from PWF on this page.'
    )
  }
  return (
    `No tracked time in the snapshot for ${from} – ${to}. ` +
    'Try a range within the last 35 days, or ask an admin to refresh the snapshot (Fetch from PWF).'
  )
}

/**
 * Resolve tracked hours from snapshot when the range matches stored buckets
 * (daily, MTD, or a full calendar month from the last 3 months).
 */
export function resolveEmployeeTimeTrackedFromSnapshot(
  from: string,
  to: string,
  snapshot: Pick<
    ProfitabilitySnapshotData,
    | 'employeeTimeSpentMtd'
    | 'employeeTimeSpentByMonth'
    | 'employeeTimeSpentByDay'
    | 'mtdTimeTrackedFrom'
    | 'mtdTimeTrackedTo'
  >
): PwfEmployeeTimeSpent[] | null {
  const fromDaily = sumEmployeeTimeFromDailyBuckets(
    from,
    to,
    snapshot.employeeTimeSpentByDay ?? {}
  )
  if (fromDaily != null) return fromDaily

  const snapMtdFrom = snapshot.mtdTimeTrackedFrom
  const snapMtdTo = snapshot.mtdTimeTrackedTo
  if (
    snapMtdFrom &&
    snapMtdTo &&
    from === snapMtdFrom &&
    to === snapMtdTo &&
    snapshot.employeeTimeSpentMtd.length > 0
  ) {
    return snapshot.employeeTimeSpentMtd
  }

  const mtd = getMonthToDateRange()
  if (from === mtd.from && to === mtd.to && snapshot.employeeTimeSpentMtd.length > 0) {
    return snapshot.employeeTimeSpentMtd
  }
  if (isFullCalendarMonthRange(from, to)) {
    const monthKey = from.slice(0, 7)
    const rows = snapshot.employeeTimeSpentByMonth[monthKey]
    if (rows?.length) return rows
  }
  return null
}

/** Active-task due dates: full calendar month on MTD preset; custom range otherwise. */
export function resolveActiveTaskDueDateRange(
  datePreset: 'mtd' | 'custom',
  dateRange: { from: string; to: string }
): { from: string; to: string } {
  if (datePreset === 'mtd') return getCalendarMonthRange()
  return dateRange
}

/** Non-complete tasks whose due date falls within the dashboard date range. */
export function filterActiveTasksByDueDateRange(
  tasks: ActiveTaskEntry[],
  dateRange: { from: string; to: string }
): ActiveTaskEntry[] {
  return tasks.filter((t) => {
    const due = t.dueDate?.trim()
    if (!due || !/^\d{4}-\d{2}-\d{2}$/.test(due)) return false
    if (dateRange.from && due < dateRange.from) return false
    if (dateRange.to && due > dateRange.to) return false
    return true
  })
}
